/* eslint-disable @typescript-eslint/camelcase */
import isDomUsable from '@fenderdigital/utils/dom';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import Heading from '@fenderdigital/ui/Heading';
import Button from '@fenderdigital/ui/Button';
import Spacer from '@fenderdigital/ui/Spacer';
import usePlayUser from '@fenderdigital/custom-hooks/hooks/usePlayUser';
import { EventNames, trackEvent } from '../../lib/analytics';
import { pathAnalyticsSelector } from '../../utils/segment-utils';
import {
  PersistentUpgradeContainer,
  HeadingLine,
  TimeLeftSpan,
  ButtonContainer,
} from './persistent-upgrade-styles';
import { calculateTimeLeft, getDurationCopy, TimeLeft } from '../../utils/date-utils';

interface PersistentUpgradeProps {
  subscriptionExpirationDate?: string | null;
}

const PersistentUpgrade: React.FC<PersistentUpgradeProps> = ({ subscriptionExpirationDate }) => {
  const history = useHistory();
  const { user } = usePlayUser();
  const isSmallScreen = !!useBreakpoint(down('phone'));
  const pathSegmentData = useSelector(pathAnalyticsSelector);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0 });

  useEffect(() => {
    if (subscriptionExpirationDate) {
      setTimeLeft(calculateTimeLeft(subscriptionExpirationDate));
    }
  }, [subscriptionExpirationDate]);

  const hideCountdown = timeLeft.hours <= 0 || timeLeft.hours > 48;
  const phrasing = timeLeft.hours > 48 ? 'is ending soon.' : 'is ending.';

  const upgradeCopy = [
    `Don't lose your progress. `,
    'Your free trial ',
    `${hideCountdown ? phrasing : 'ends in '}`,
    `${hideCountdown ? '' : getDurationCopy(timeLeft)}`,
  ];
  const ctaButtonCopy = isSmallScreen ? 'Upgrade' : 'Upgrade Now';

  const segmentData = {
    ...pathSegmentData,
    fc_id: user?.userID,
    ad_provider: 'fender_internal',
    ad_identifier: 'freemium_upgrade_persistent_banner_2_with_countdown',
    ad_bodycopy: upgradeCopy.join(''),
    ad_displaychance: 100,
    ad_filename: '',
    ad_headlinecopy: `${upgradeCopy[0]} ${upgradeCopy[1]} ${upgradeCopy[2]} ${upgradeCopy[3]}`,
    cta_buttoncopy: ctaButtonCopy,
    cta_buttoncolor: '#FF9B00', // use HEX for analytics;
    referrer: isDomUsable() ? window.location.pathname : '',
    test_drive_days_remaining: timeLeft.days,
    test_drive_hours_remaining: timeLeft.hours,
  };

  const handleClick = (): void => {
    history.push('/plans');
    trackEvent(EventNames.SUBSCRIPTION_AD_BANNER_CLICKED, segmentData);
  };

  return (
    <PersistentUpgradeContainer data-id="freemium-upgrade-banner">
      <Heading
        as="h2"
        textColor="white"
        fontFamily="futuraDemi"
        textSize={['f1', 'f3', null, 'f4']}
      >
        <HeadingLine>{upgradeCopy[0]}</HeadingLine>
        <HeadingLine>
          {upgradeCopy[1]} {upgradeCopy[2]}
          {!hideCountdown && <TimeLeftSpan>{upgradeCopy[3]}</TimeLeftSpan>}
        </HeadingLine>
      </Heading>

      <Spacer direction="vertical" spacing="md1" />

      <ButtonContainer>
        <Button
          shape="pill"
          colorScheme="orange"
          dataId="freemium-banner-upgrade-button"
          onClick={handleClick}
        >
          {ctaButtonCopy}
        </Button>
      </ButtonContainer>
    </PersistentUpgradeContainer>
  );
};

export default PersistentUpgrade;
